// useSwipeableFeed.js
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const WINDOW_CONFIG = {
  1: { prev: 0, next: 0 }, // [current]
  2: { prev: 0, next: 1 }, // [current, +1]
  3: { prev: 1, next: 1 }, // [-1, current, +1]
  4: { prev: 1, next: 2 }, // [-1, current, +1, +2]
  5: { prev: 2, next: 2 }, // [-2, -1, current, +1, +2]
};

const useSwipeableFeed = ({
  items,
  windowSize = 1,
  initialActiveId,
  onActiveChange,
}) => {
  const [activeId, setActiveId] = useState(
    () => initialActiveId || items?.[0]?.id || null
  );

  const activeIndex = useMemo(
    () => (items || []).findIndex(item => item.id === activeId),
    [items, activeId]
  );

  const clampedWindowSize = Math.min(Math.max(windowSize, 1), 5);
  const { prev: prevCount, next: nextCount } = WINDOW_CONFIG[clampedWindowSize];

  const getItemState = useCallback(
    index => {
      if (activeIndex === -1) {
        return { isActive: false, isPreloading: false, slotIndex: -1 };
      }
      const isActive = index === activeIndex;
      const distance = index - activeIndex;
      let slotIndex = -1;
      if (distance >= -prevCount && distance <= nextCount) {
        slotIndex = distance + prevCount;
      }
      const isPreloading = slotIndex !== -1 && !isActive;
      return { isActive, isPreloading, slotIndex };
    },
    [activeIndex, prevCount, nextCount]
  );

  const goToNext = useCallback(() => {
    const nextItem = items?.[activeIndex + 1];
    if (!nextItem) return false;
    setActiveId(nextItem.id);
    return true;
  }, [items, activeIndex]);

  const goToPrev = useCallback(() => {
    const prevItem = items?.[activeIndex - 1];
    if (!prevItem) return false;
    setActiveId(prevItem.id);
    return true;
  }, [items, activeIndex]);

  useEffect(() => {
    if (items?.length > 0 && activeId === null) {
      setActiveId(items[0].id);
    }
  }, [items, activeId]);

  useEffect(() => {
    if (!initialActiveId) return;
    setActiveId(initialActiveId);
  }, [initialActiveId]);

  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;

  useEffect(() => {
    if (activeId) {
      onActiveChangeRef.current?.(activeId, activeIndex);
    }
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    activeId,
    activeIndex,
    getItemState,
    goToNext,
    goToPrev,
  };
};

export default useSwipeableFeed;
